import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";

import { cx } from "@tripcart/traveler-ui/cx";

import styles from "./container.module.css";

/**
 * The single source of truth for storefront horizontal layout.
 *
 * Every aligned edge on the site — header, page body, footer — flows
 * through this one width + gutter so the left/right margins are identical
 * on every route. Width is 1280px, the style guide's `.wrap`; gutters are
 * 16px (mobile) / 32px (md+). `ChromeContainer` carries the same contract
 * for the header and footer.
 *
 * Inner content that needs to be narrower for readability (legal prose,
 * centered CTAs) nests its own max-width INSIDE a Container so its gutters
 * still line up with everything else.
 *
 * Never re-implement this width + gutter inline in a page — use this.
 */
export const SITE_CONTAINER = styles.site;

type ContainerProps<T extends ElementType> = {
  as?: T;
  className?: string;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "className" | "children">;

export function Container<T extends ElementType = "div">({
  as,
  className,
  children,
  ...props
}: ContainerProps<T>) {
  const Comp = (as ?? "div") as ElementType;
  return (
    <Comp className={cx(SITE_CONTAINER, className)} {...props}>
      {children}
    </Comp>
  );
}
